import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import { Injectable } from "@angular/core";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import { IAPIListResult, IPagination, IPaginationResult } from "app/common/interfaces";
import { andAllWhere, andWhere, buildQuery, CombinedFilter, FilterFn, noop, Ops, orderBy, OrderVariant, orWhere, page, where } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import OrderPaymentAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/process/order-payment/order-payment.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";

const apiUrl = env.API_PAYMENT_ORDER_URL;
const moduleName = "order_payment";

@Injectable({
  providedIn: "root",
})
export class PaymentOrderService extends AbstractService<any, any, any> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<OrderPaymentAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * List
   * @param filter CombinedFilter of related entity - Order
   * @returns Data list
   */
  getPaginatePayment(
    options: IPagination<OrderPaymentAttributes>,
    date,
    routeIds: number[],
    status: number[],
    statusERP: number[],
    dataFilter?
  ): Observable<IPaginationResult<OrderPaymentAttributes>> {
    const arrayQueries: FilterFn<OrderPaymentAttributes>[] = [];
    if (dataFilter.paymentTypeIds.length>0) {
      arrayQueries.push(andAllWhere("paymentType.code" as keyof OrderPaymentAttributes, Ops.in(...dataFilter.paymentTypeIds)))
    }
    if (dataFilter.paymentMethodIds.length>0) {
      arrayQueries.push(andAllWhere("paymentMethod.code" as keyof OrderPaymentAttributes, Ops.in(...dataFilter.paymentMethodIds)))
    }
    if (dataFilter.bankIds.length>0) {
      arrayQueries.push(andAllWhere("bank.code" as keyof OrderPaymentAttributes, Ops.in(...dataFilter.bankIds)))
    }
    if (dataFilter.businessIds.length>0) {
      arrayQueries.push(andAllWhere("shipTo.id" as keyof OrderPaymentAttributes, Ops.in(...dataFilter.businessIds)))
    }
    const query = buildQuery<OrderPaymentAttributes>(
      // where('_deleted', Ops.eq(false)),
      where("user.user_login.username" as keyof OrderPaymentAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("subRoute.name" as keyof OrderPaymentAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("serials" as keyof OrderPaymentAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.name" as keyof OrderPaymentAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.externalKeys.code" as keyof OrderPaymentAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("operationNumber" as keyof OrderPaymentAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("document" as keyof OrderPaymentAttributes, Ops.like(options.search ?? "", "i")),
      routeIds && routeIds.length > 0 ? andAllWhere('subRoute.id' as keyof OrderPaymentAttributes, Ops.in(...routeIds)) : noop(),
      status && status.length > 0 ? andAllWhere('paymentReview' as keyof OrderPaymentAttributes, Ops.in(...status)) : noop(),
      statusERP && statusERP.length > 0 ? andAllWhere('paymentStatus' as keyof OrderPaymentAttributes, Ops.in(...statusERP)) : noop(),
      date.start && date.end ? andAllWhere("dateHour", Ops.btwnDates(date.start, date.end)) : noop(),
      dataFilter.tripStart.tripStart && dataFilter.tripStart.tripEnd
        ? andAllWhere("trip.dateHourStart" as keyof OrderPaymentAttributes, Ops.btwnDates(dataFilter.tripStart.tripStart, dataFilter.tripStart.tripEnd))
        : noop(),
      dataFilter.tripEnd.startTripEnd && dataFilter.tripEnd.endTripEnd
        ? andAllWhere("trip.dateHourEnd" as keyof OrderPaymentAttributes, Ops.btwnDates(dataFilter.tripEnd.startTripEnd, dataFilter.tripEnd.endTripEnd))
        : noop(),
      
      ...arrayQueries,

      orderBy(
        options.sort ?? "trip.dateHourStart" as keyof OrderPaymentAttributes,
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string = apiUrl +
      `api/${moduleName}/withCountByOrder?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<any>>(API_URL);
  }


  findByFilter(filter: CombinedFilter<OrderPaymentAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCountByOrder?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
    return this.http.get<IPaginationResult<OrderPaymentAttributes>>(API_URL, { headers });
  }

  /**
    * Pagination
    * @param options Pagination options
    * @returns Paginated data
    */
  findAllWithCountByTrip(
    options: IPagination<OrderPaymentAttributes>,
    tripId: number,
    document_type: string[]
  ): Observable<IPaginationResult<OrderPaymentAttributes>> {
    const query = buildQuery<OrderPaymentAttributes>(
      // where('_deleted', Ops.eq(false)),
      where('document_type.code' as keyof OrderPaymentAttributes, Ops.in(...document_type)),
      andWhere('trip.id' as keyof OrderPaymentAttributes, Ops.eq(tripId)),
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<OrderPaymentAttributes>>(API_URL);
  }
}
